import {Inject, Injectable, inject} from "@angular/core";
import {HttpClient, HttpParams, HttpResponse} from "@angular/common/http";
import {Observable} from "rxjs";
import {AiFinisherConfig, AiSuggestion, AiWorkResponse} from "../models/ai-suggestion-model";

/**
 * Thin client over every dcf-api endpoint that exposes the AI tuning "finisher"
 * automation. Stateless — the container component owns the view state (signals);
 * auth/credentials are added by the global AuthInterceptor.
 */
@Injectable({providedIn: "root"})
export class AiFinisherService {

    private httpClient = inject(HttpClient);

    constructor(@Inject('API_URL') private API_URL: string) {
    }

    /** The review queue. Optional `type` maps to the API's suggestion_type filter. */
    listSuggestions(type?: string): Observable<AiSuggestion[]> {
        let params = new HttpParams();
        if (type) {
            params = params.set('type', type);
        }
        return this.httpClient.get<AiSuggestion[]>(`${this.API_URL}/ai-suggestions`, {params});
    }

    /** Full detail of one suggestion (rationale, diff, checksum proof, statuses). */
    getSuggestion(id: number): Observable<AiSuggestion> {
        return this.httpClient.get<AiSuggestion>(`${this.API_URL}/ai-suggestions/${id}`);
    }

    /** The AI work + full ordered decision log behind an import's AI icon. */
    getAiWork(importId: number): Observable<AiWorkResponse> {
        return this.httpClient.get<AiWorkResponse>(`${this.API_URL}/imports/${importId}/ai-work`);
    }

    /** All suggestions the AI produced for one client import (newest first) — the imports-table AI popup. */
    getSuggestionsForImport(importId: number): Observable<AiSuggestion[]> {
        return this.httpClient.get<AiSuggestion[]>(`${this.API_URL}/imports/${importId}/ai-suggestions`);
    }

    /** Approve — flips approval_status to APPROVED; dcf-main-app finalises later. */
    approve(id: number, approver?: string): Observable<AiSuggestion> {
        let params = new HttpParams();
        if (approver) {
            params = params.set('approver', approver);
        }
        return this.httpClient.post<AiSuggestion>(`${this.API_URL}/ai-suggestions/${id}/approve`, null, {params});
    }

    /** Reject — records the reason as feedback. */
    reject(id: number, reason?: string): Observable<AiSuggestion> {
        let params = new HttpParams();
        if (reason) {
            params = params.set('reason', reason);
        }
        return this.httpClient.post<AiSuggestion>(`${this.API_URL}/ai-suggestions/${id}/reject`, null, {params});
    }

    /** Download the finished bundle zip (Original + Original.ini + loose versions). */
    downloadBundle(id: number): Observable<HttpResponse<Blob>> {
        return this.httpClient.get(`${this.API_URL}/ai-suggestions/${id}/download`, {
            observe: 'response',
            responseType: 'blob'
        });
    }

    /**
     * Hand a pure original's (partial) tuning to the AI finisher — dcf-api proxies dcf-main-app's seed,
     * which creates the OPEN tuning_handoff dcf-automation claims. Idempotent per pure original + tuning.
     * Returns the handoff row (id, workStatus, …).
     */
    seedFromPureOriginal(pureOriginalId: number, tuning?: string): Observable<any> {
        let params = new HttpParams();
        if (tuning) {
            params = params.set('tuning', tuning);
        }
        return this.httpClient.post<any>(
            `${this.API_URL}/ai-finisher/seed/from-pure-original/${pureOriginalId}`, null, {params});
    }

    /** The AI Finisher on/off switch state. */
    getFinisherConfig(): Observable<AiFinisherConfig> {
        return this.httpClient.get<AiFinisherConfig>(`${this.API_URL}/ai-finisher/config`);
    }

    /** Flip the switch — dcf-main-app stops/starts seeding handoffs; dcf-automation stops/starts claiming. */
    setFinisherEnabled(enabled: boolean, updatedBy?: string): Observable<AiFinisherConfig> {
        let params = new HttpParams().set('enabled', enabled);
        if (updatedBy) {
            params = params.set('updatedBy', updatedBy);
        }
        return this.httpClient.post<AiFinisherConfig>(`${this.API_URL}/ai-finisher/config`, null, {params});
    }
}
